// 24h timeline scrubber (spec 17 §3.1/§4.3). REF: lsappv3 SDPlaybackView `sd_timeline`
// (recording blocks by day-fraction) + Flutter `hls_timebar` (track + playhead + eventMarker).
// Recording segments render as blocks; events render as markers on the track; a draggable
// playhead seeks. Pointer down/move on the track → onSeek(µs). Shared across App shells.

import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTimestampUs } from '../../lib/format';
import { clusterEvents, coalesceSegments, formatTimestampTz, fractionToUs, rulerTicks, usToFraction, type ClipSpan, type TimeRange } from './timeline';
import './playback.css';

export interface TimelineEvent {
  event_ts: number;
  label?: string;
  /** type emoji for the annotation marker (eventsLib cluster icon). */
  icon?: string;
  /** localized type label, shown in the hover tooltip. */
  typeLabel?: string;
  /** record mode the event was emitted in (continuous/event/…), shown in the tooltip. */
  mode?: string;
}

interface Props {
  range: TimeRange;
  clips: ClipSpan[];
  events: TimelineEvent[];
  /** Current playhead position (µs epoch) or null when nothing is playing. */
  playheadUs: number | null;
  /**
   * Preview a µs position (clamped to the window). Fires continuously while the
   * playhead is being dragged — move the red line only, do NOT start playback here.
   */
  onSeek: (us: number) => void;
  /**
   * Commit a µs position once the drag is released (pointer up / click / arrow key).
   * This is where playback should be (re)started. When omitted, `onSeek` is used as
   * the commit too — so legacy callers that play live during the drag keep working.
   */
  onSeekCommit?: (us: number) => void;
  /**
   * Opt-in: render events as annotation markers (leader line + type box) above the track
   * instead of plain in-track ticks. Off by default so existing callers are unaffected.
   */
  annotateEvents?: boolean;
  /** A single event marker was activated → seek/play near it (µs of the event). */
  onEventActivate?: (eventTsUs: number) => void;
  /**
   * A clustered marker was activated. centerTsUs = where to center the zoom-in; earliestTsUs
   * = its first event (play target when it can't be zoomed further); spanUs = its time spread.
   * The handler steps the zoom down one level to spread the group out.
   */
  onClusterZoom?: (centerTsUs: number, earliestTsUs: number, spanUs: number) => void;
  /**
   * Display timezone as a UTC offset in minutes. When set, ruler labels and the playhead
   * clock render in that zone; when omitted, times use the browser locale (legacy callers).
   */
  tzOffsetMin?: number;
}

export function TimelineStrip({ range, clips, events, playheadUs, onSeek, onSeekCommit, annotateEvents, onEventActivate, onClusterZoom, tzOffsetMin }: Props) {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);

  // Time rendering: tz-aware when an offset is given (continuous view), else browser locale.
  const fmtTime = (us: number) => (tzOffsetMin === undefined ? formatTimestampUs(us) : formatTimestampTz(us, tzOffsetMin));

  const segments = coalesceSegments(clips, range);
  const ticks = rulerTicks(range, 120, (tzOffsetMin ?? 0) * 60_000);
  const playheadFraction = playheadUs !== null ? usToFraction(playheadUs, range) : null;
  const eventClusters = annotateEvents
    ? clusterEvents(
        events.map((e) => ({ tsUs: e.event_ts, icon: e.icon, typeLabel: e.typeLabel, mode: e.mode })),
        range
      )
    : [];

  const usFromClientX = useCallback(
    (clientX: number): number | null => {
      const el = trackRef.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      const fraction = (clientX - rect.left) / rect.width;
      return fractionToUs(fraction, range);
    },
    [range]
  );
  // Commit falls back to onSeek when no dedicated commit handler is supplied.
  const commit = onSeekCommit ?? onSeek;

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      draggingRef.current = true;
      e.currentTarget.setPointerCapture(e.pointerId);
      const us = usFromClientX(e.clientX);
      if (us !== null) onSeek(us); // preview the press position
    },
    [usFromClientX, onSeek]
  );
  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      const us = usFromClientX(e.clientX);
      if (us !== null) onSeek(us); // preview only — playback waits for release
    },
    [usFromClientX, onSeek]
  );
  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* not captured */
      }
      const us = usFromClientX(e.clientX);
      if (us !== null) commit(us); // released → (re)start playback at the new spot
    },
    [usFromClientX, commit]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (playheadUs === null) return;
      const step = 30_000_000; // 30s
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        commit(Math.min(range.toUs, playheadUs + step));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        commit(Math.max(range.fromUs, playheadUs - step));
      }
    },
    [commit, playheadUs, range.fromUs, range.toUs]
  );

  return (
    <div className="pb-timeline" aria-label={t('playback.timelineLabel')}>
      {/* Event annotation band (opt-in): leader line + type box above the track; dense
          events coalesce into a counted cluster. Click a single → seek/play near it; click
          a cluster → zoom in to expand. Hover shows type + time. */}
      {annotateEvents && eventClusters.length > 0 && (
        <div className="pb-timeline-events">
          {eventClusters.map((c) => {
            const isCluster = c.count > 1;
            const lastTs = c.items[c.items.length - 1].tsUs;
            const clock = (us: number) => fmtTime(us).slice(11); // HH:MM:SS part only
            const center = Math.round((c.startTsUs + lastTs) / 2);
            const evMode = c.items[0].mode;
            const tip = isCluster
              ? `${t('recPlayback.eventsCount', { count: c.count })} · ${clock(c.startTsUs)}–${clock(lastTs)}`
              : `${c.items[0].typeLabel ?? ''} · ${fmtTime(c.startTsUs)}${evMode ? ` · ${evMode}` : ''}`.trim();
            return (
              <button
                key={c.startTsUs}
                type="button"
                className={`pb-anno${isCluster ? ' pb-anno--cluster' : ''}`}
                style={{ left: `${c.fraction * 100}%` }}
                onClick={() => (isCluster ? onClusterZoom?.(center, c.startTsUs, lastTs - c.startTsUs) : onEventActivate?.(c.startTsUs))}
                aria-label={tip}
              >
                <span className="pb-anno-box">{isCluster ? c.count : c.icon ?? '📌'}</span>
                <span className="pb-anno-line" aria-hidden />
                <span className="pb-anno-tip" role="tooltip">
                  {tip}
                </span>
              </button>
            );
          })}
        </div>
      )}
      <div
        ref={trackRef}
        className="pb-timeline-track"
        role="slider"
        tabIndex={0}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={playheadFraction !== null ? Math.round(playheadFraction * 100) : 0}
        aria-valuetext={playheadUs !== null ? fmtTime(playheadUs) : undefined}
        aria-label={t('playback.timelineTrackLabel')}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onKeyDown={onKeyDown}
      >
        {/* recording blocks */}
        {segments.map((seg) => (
          <span
            key={seg.startUs}
            className="pb-timeline-seg"
            style={{ left: `${seg.startFraction * 100}%`, width: `${Math.max(0.3, seg.widthFraction * 100)}%` }}
            aria-hidden
          />
        ))}
        {/* Plain in-track event ticks (legacy mode). When annotateEvents is on, events are
            drawn in the annotation band above instead. Filtered to the visible window so
            off-window events don't pile up at the 0%/100% edges (usToFraction clamps). */}
        {!annotateEvents &&
          events
            .filter((ev) => ev.event_ts >= range.fromUs && ev.event_ts <= range.toUs)
            .map((ev, i) => (
              <span
                key={`${ev.event_ts}-${i}`}
                className="pb-timeline-marker"
                style={{ left: `${usToFraction(ev.event_ts, range) * 100}%` }}
                title={ev.label}
                aria-hidden
              />
            ))}
        {/* playhead */}
        {playheadFraction !== null && (
          <span className="pb-timeline-playhead" style={{ left: `${playheadFraction * 100}%` }} aria-hidden />
        )}
      </div>
      <div className="pb-timeline-ruler" aria-hidden>
        {ticks.map((tk, i) => (
          <span
            key={i}
            className={`pb-timeline-tick${tk.major ? ' pb-timeline-tick--major' : ''}`}
            style={{ left: `${tk.fraction * 100}%` }}
          >
            {tk.major && tk.label ? <span className="pb-timeline-tick-label">{tk.label}</span> : null}
          </span>
        ))}
      </div>
    </div>
  );
}
